import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_MOTION_LEVEL,
  isMotionLevel,
  resolveReducedMotion,
  type MotionLevel,
} from '@/design/motion/motion-level';
import { usePrefersReducedMotion } from '@/lib/use-prefers-reduced-motion';

/**
 * 動畫效果等級只存在 localStorage，不進 IndexedDB、不匯出（data-model 8.2）。
 * 與主題一樣用純字串保存，index.html 的開場腳本在第一次繪製前就會讀它，
 * 避免選了「減少」的人每次載入都先看到一瞬間的動畫。
 */
export const MOTION_STORAGE_KEY = 'ui.motion.level';

interface MotionContextValue {
  level: MotionLevel;
  setLevel: (level: MotionLevel) => void;
  /** 綜合等級與系統設定之後，這次要不要減少動態。 */
  reducedMotion: boolean;
}

const MotionContext = createContext<MotionContextValue | null>(null);

function readStoredLevel(): MotionLevel {
  try {
    const stored = localStorage.getItem(MOTION_STORAGE_KEY);
    return isMotionLevel(stored) ? stored : DEFAULT_MOTION_LEVEL;
  } catch {
    return DEFAULT_MOTION_LEVEL;
  }
}

export function MotionProvider({ children }: { children: ReactNode }) {
  const [level, setLevelState] = useState<MotionLevel>(readStoredLevel);
  const systemPrefersReduce = usePrefersReducedMotion();
  const reducedMotion = resolveReducedMotion(level, systemPrefersReduce);

  // CSS 那半邊靠 data-motion 決定；JavaScript 排程的動畫則讀 reducedMotion。
  useEffect(() => {
    document.documentElement.dataset['motion'] = level;
  }, [level]);

  const setLevel = useCallback((next: MotionLevel) => {
    setLevelState(next);
    try {
      localStorage.setItem(MOTION_STORAGE_KEY, next);
    } catch {
      // 無法保存偏好時仍讓本次切換生效。
    }
  }, []);

  const value = useMemo<MotionContextValue>(
    () => ({ level, setLevel, reducedMotion }),
    [level, setLevel, reducedMotion],
  );

  return <MotionContext value={value}>{children}</MotionContext>;
}

export function useMotion(): MotionContextValue {
  const context = useContext(MotionContext);
  if (!context) throw new Error('useMotion 必須放在 MotionProvider 之內');
  return context;
}

/**
 * 給「只想知道要不要動」的元件用。
 *
 * 沒有 Provider 時回傳 `false`（照常播動畫），與預設等級 `full` 一致——
 * 這樣元件在測試或 Storybook 裡單獨渲染也不會 throw。
 */
export function useReducedMotion(): boolean {
  return useContext(MotionContext)?.reducedMotion ?? false;
}
